/**
 * Lokalni kopie tabulky POVINNYCH BRAN — MUSI zustat shodna s
 * @reos/duediligence `trust.MANDATORY_GATES_BY_CASE_KIND` (overuje
 * `gate-catalog.spec.ts` porovnanim se skutecnym balickem).
 *
 * Duvod duplikace je stejny jako u `answer-source.ts` (BLOK 10): web-admin smi
 * z @reos/duediligence importovat jen TYPY (`import type`, erasovane pri
 * kompilaci). Balicek nema `"sideEffects": false` a jeho jediny vstupni bod
 * (`dist/index.js`) re-exportuje i server-only kod
 * (`trust/persist.js` -> @reos/observability -> `node:async_hooks`) — runtime
 * import odsud rozbiji prohlizecovy build Vite/Rollup (`AsyncLocalStorage is
 * not exported by __vite-browser-external`). Hlida to
 * `test/browser-safe-imports.spec.ts`.
 */

import type { GateCaseKind } from './case-subject.js';

/**
 * Povinne brany podle typu zakazky — zrcadlo
 * `trust.MANDATORY_GATES_BY_CASE_KIND` (aggregate.ts:179). Poradi je soucasti
 * smlouvy: prvni neuzavrena brana v tomto poradi je ta, kterou
 * `decisionReadiness.reason` pojmenuje.
 */
export const MANDATORY_GATES_BY_CASE_KIND: Readonly<Record<GateCaseKind, readonly string[]>> = {
  LAND: ['ownership', 'transaction_scope', 'legal_access', 'sewer_capacity'],
  HOUSE: [
    'ownership',
    'transaction_scope',
    'legal_access',
    'building_legality',
    'structural_safety',
  ],
  FLAT: ['ownership', 'transaction_scope', 'building_legality'],
};

/**
 * Claim typy, pro ktere ma katalog lidskou vetu
 * (`webAdmin.caseCockpit.issue.gate.<claimType>`). Je to tentyz vycet jako
 * `CLAIM_TYPE_CAPTIONS` v @reos/reports — tedy VSECHNY brany, ktere registr
 * umi zalozit, ne jen ty povinne (`easements`, `liens`, `utility_capacity`,
 * `zoning_applicability`, `realized_price` a `professional_technical` povinne
 * nejsou, ale na kokpitu se objevit mohou).
 */
/**
 * Verze pravidel duvery, podle ktere pocita DNESNI pipeline — zrcadlo
 * `trustPolicyVersion` (`internal/trust/registry/index.ts:33`).
 *
 * Krok UX2, sekce Aktualnost: beh, ktery probehl podle STARYCH pravidel, je
 * zastaraly bez ohledu na datum. `dd_trust_assessments.policy_version` chodi
 * do detailu jako `fixture.trust.policyVersion`, takze staci porovnat.
 * Parita s balickem je v `gate-catalog.spec.ts`.
 */
export const CURRENT_TRUST_POLICY_VERSION = 'trust-policy-2026-08-31';

/**
 * Krok UXR3 — DRUHY DOKLADU, KTERE SI REOS DOTAHNE SAM.
 *
 * Zadani UXR3 bod 5: oblast proverení ma akci „Dohledat" tam, „kde to
 * collector umi, jinak »Zadat«". Rozhodnuti nestoji na dohadu — opira se o
 * dve veci, ktere uz v repu jsou:
 *
 *  1. `trust/raise.ts` deli druhy dokladu na `ASSISTED` („umi system
 *     dotahnout asistovane, verejne API"), `EXTERNAL` (vyzada se urad nebo
 *     spravce site) a `MANUAL`. Paritu hlida `gate-catalog.spec.ts` pres
 *     `trust.whatRaisesConfidence` — tedy pres skutecny vypocet balicku, ne
 *     pres opsany seznam.
 *  2. Z `ASSISTED` druhu si REOS SAM nedotahne ty, pro ktere zaklada LIDSKY
 *     UKOL (`dd_human_tasks`), protoze strojovou cestu zdroj nema nebo ji
 *     zakazuje robots.txt (stav `HUMAN_ONLY`, krok K0e): `LV` a `CUZK_DP`
 *     maji `cuzk.nahlizeni`/`cuzk.dpn` a jsou v `KNOWN_HUMAN_TASK_SOURCES`
 *     (`lib/case-cockpit.ts`). U nich je poctiva akce „Zadat", ne „Dohledat".
 *
 * Kdyz se zdroji otevre strojova cesta, pribude druh sem a nikam jinam.
 *
 * KROK ISIR1b — `INSOLVENCY_REGISTER` uz TU JE. Do ISIR1 chybel proto, ze
 * strojova cesta bezela jen u PRAVNICKE osoby, zatimco u FYZICKE byla za
 * konfiguraci (vychozi vypnuto) a vetsina vlastniku jsou fyzicke osoby —
 * „Dohledat" by tedy vetsinou nic nedohledalo. Rozhodnutim R.B. ze 4. 10. 2026
 * je lustrace fyzickych osob ZAPNUTA (`OwnerInsolvencyAdapter` nad oficialni
 * webovou sluzbou ISIR, zdroj `isir.ws.osoba`), takze akce „Dohledat" uz je
 * poctiva. `isir.public` zustava v `KNOWN_HUMAN_TASK_SOURCES` kvuli UZ
 * ZALOZENYM ukolum v zive DB (duvod je u te konstanty).
 */
export const SELF_FETCHED_EVIDENCE_KINDS: readonly string[] = [
  // Hranice parcel z ČÚZK INSPIRE (WFS) — tahá je geometricky collector,
  // `trust/persist.ts` mapuje zdroj `cuzk.inspire.cp` na tenhle druh.
  'INSPIRE_GEOMETRY',
  // Obchodní rejstřík ARES — veřejné REST API bez registrace (krok FIX6 9(a)).
  'ARES',
  // Insolvenční rejstřík — oficiální webová služba MSp zdarma a bez registrace
  // (krok ISIR1b, zdroje `isir.ws` + `isir.ws.osoba`).
  'INSOLVENCY_REGISTER',
];

/**
 * Krok UXR5 bod 11 — DRUHY DOKLADU, KTERE OBLAST UZAVIRAJI, PODLE JEJIHO
 * TYPU. Zrcadlo `trust.GATES_REGISTRY` (`closingEvidenceKinds`); paritu s
 * balickem hlida `gate-catalog.spec.ts`, takze pridani brany do registru bez
 * doplneni sem spadne v testu.
 *
 * Proc to admin potrebuje: do UXR5 se „Dohledat" rozhodovalo z
 * `gate.closableBy` NA BEHU zakazky. Jenze seznam bran je v produkci PRAZDNY
 * (NALEZ 1 kroku UX1, podrobne v hlavicce `case-gates.ts`), takze oblast bez
 * vyhodnocene brany dostala vzdy „Zadat" — i kdyz je to prave ta, kterou si
 * REOS umi dotahnout sam (uzemni plan z INSPIRE geometrie). Druh dokladu
 * ale na BEHU nezavisi: je to vlastnost TYPU oblasti, a ta je v registru.
 */
export const GATE_CLOSING_EVIDENCE_BY_CLAIM_TYPE: Readonly<Record<string, readonly string[]>> = {
  ownership: ['LV', 'CUZK_DP'],
  transaction_scope: ['PURCHASE_CONTRACT', 'DECLARATION_OF_OWNER'],
  legal_access: ['LV', 'CUZK_DP', 'EASEMENT_CONTRACT', 'PUBLIC_ROAD_REGISTRY'],
  easements: ['EASEMENT_CONTRACT', 'LV', 'CUZK_DP'],
  liens: ['LV', 'CUZK_DP', 'INSOLVENCY_REGISTER'],
  building_legality: ['OCCUPANCY_APPROVAL'],
  zoning_applicability: ['INSPIRE_GEOMETRY'],
  utility_capacity: ['UTILITY_OPERATOR_STATEMENT'],
  sewer_capacity: ['UTILITY_OPERATOR_STATEMENT'],
  realized_price: ['PURCHASE_CONTRACT'],
  structural_safety: ['INSPECTION_REVISION'],
  professional_technical: ['INSPECTION_REVISION', 'PENB'],
};

/**
 * Umi si REOS doklad teto oblasti dotahnout SAM? Jedina funkce, kterou se to
 * rozhoduje — vola ji seznam oblasti („Dohledat" vs. „Zadat") i karta
 * „Další krok" („Dohledat automaticky (N)"), takze tlacitko a cislo vedle nej
 * nemohou rict dve ruzne veci.
 *
 * `closableBy` z BEHU ma prednost (kdyz beh brany vyhodnotil, je presnejsi —
 * zna i doklady, ktere uz u zakazky lezi); kdyz beh brany nema, plati druhy
 * dokladu podle TYPU oblasti z registru vyse.
 */
export function isSelfFetchableClaim(
  claimType: string,
  closableBy: readonly string[] | undefined,
): boolean {
  // PRAZDNY seznam z behu se bere jako „beh o te brane nic nerekl" — ne jako
  // „nic ji nezavre". Prave tenhle tvar chodi z produkce.
  const kinds =
    closableBy !== undefined && closableBy.length > 0
      ? closableBy
      : (GATE_CLOSING_EVIDENCE_BY_CLAIM_TYPE[claimType] ?? []);
  return kinds.some((kind) => SELF_FETCHED_EVIDENCE_KINDS.includes(kind));
}

export const GATE_SENTENCE_CLAIM_TYPES: readonly string[] = [
  'ownership',
  'transaction_scope',
  'legal_access',
  'sewer_capacity',
  'easements',
  'liens',
  'building_legality',
  'structural_safety',
  'zoning_applicability',
  'utility_capacity',
  'realized_price',
  'professional_technical',
];

/**
 * Krok PIL1 (vada V7) — klic faktu „RÚIAN hlási mene bytu, nez je cislo
 * pozadovane jednotky". Lokalni kopie ze stejneho duvodu jako tabulka bran
 * vyse (hlavicka souboru); paritu proti `@reos/duediligence`
 * `UNIT_FLATS_CONFLICT_FACT_KEY` hlida `gate-catalog.spec.ts`.
 */
export const UNIT_FLATS_CONFLICT_FACT_KEY = 'building_condition.flats_count_conflict';
